import {
  AgentState,
  LiveKitRoomProps,
  LKFeatureContext,
  RoomAudioRenderer,
  RoomContext,
  useLiveKitRoom,
  useLocalParticipant,
  useMultibandTrackVolume,
  useParticipantTracks,
  usePersistentUserChoices,
  useTrackTranscription,
  useVoiceAssistant
} from '@livekit/components-react'

import { LocalAudioTrack, LocalTrackPublication, RoomEvent, Track } from 'livekit-client'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Layout } from '../pages/landing'
import DataMessageSender from '../services/dataMessageSender'
import { AudioNode, ImageNode, RedirectNode, SilentNode, VideoNode } from '../types/nodeTypes'
// import CanvasCircularWaves from './animations/CanvasCircle'
// import { CanvasWaveform } from './animations/CanvasWaveForm'
import { BarsWaveForm } from './animations/BarsForm'
import LatencyDisplay from './LatencyDisplay'
import { NoAgentNotification } from './NoAgentNotification'
import AudioPlayer from './nodes/Audio'
import Image from './nodes/Image'
import Silence from './nodes/Silence'
import Video from './nodes/Video'
import { QuestionOptions } from './QuestionOptions'
import TextAnswerer from './TextAnswerer'
import toast from './Toast'
import { DataMessage } from './VoiceRoom'
// import NeonWaveVisualizer from './animations/NewAgenWave'
// import CelestialCanvas from './animations/SphereAnimation'
import DisconnectSurveyButton from './buttons/DisconnectSurveyButton'
import TrackToggle from './buttons/TrackToggle'
import VoiceVisualization from './animations/newLovable'
// import { useKrispNoiseFilter } from '@livekit/components-react/krisp'
import { isKrispNoiseFilterSupported } from '@livekit/krisp-noise-filter'

// import NewAgentWaves from './animations/NewAgenWave'

const VocadsAssistant: (
  props: React.PropsWithChildren<LiveKitRoomProps> &
    React.RefAttributes<HTMLDivElement> & {
      conversationId: string | undefined
      isConnected: boolean
      activeLayout: Layout
      handleEndOpen?: () => void
      primaryColor: string
      secondaryColor: string
    }
) => React.ReactNode = /* @__PURE__ */ React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<
    LiveKitRoomProps & {
      conversationId: string | undefined
      isConnected: boolean
      activeLayout: Layout
      handleEndOpen?: () => void
      primaryColor: string
      secondaryColor: string
    }
  >
>(function VocadsAssistant(
  props: React.PropsWithChildren<
    LiveKitRoomProps & {
      conversationId: string | undefined
      isConnected: boolean
      activeLayout: Layout
      handleEndOpen?: () => void
      primaryColor: string
      secondaryColor: string
    }
  >,
  ref
) {
  const { room, htmlProps } = useLiveKitRoom(props)
  const { handleEndOpen } = props
  let messageSender: DataMessageSender | undefined = undefined
  if (room) {
    messageSender = new DataMessageSender(room)
  }

  const [options, setOptions] = useState<string[] | undefined>(undefined)
  const [selectedOption, setSelectedOption] = useState<number | undefined>(undefined)
  const [textResponse, setTextResponse] = useState<string | undefined>(undefined)
  const [dataMessage, setDataMessage] = useState<DataMessage | undefined>(undefined)
  const [accumulatedResponses, setAccumulatedResponses] = useState<string[]>([])
  const [transcriptCommitted, setTranscriptCommitted] = useState<boolean>(false)
  const [previousMicrophoneState, setPreviousMicrophoneState] = useState<boolean>(false)
  const [micClosedBySystem, setMicClosedBySystem] = useState<boolean>(false)
  const [micEvent, setMicEvent] = useState<string | undefined>(undefined)
  const handleOptionClick = (idx: number): void => {
    setSelectedOption(idx)
    if (options !== undefined) messageSender?.sendResponse(options[idx] ?? '')
  }

  const searchParams = new URLSearchParams(window.location.search)

  const campaignId = searchParams.get('id')
  // const thirdpartyId = searchParams.get('v_ref')

  const preview = searchParams.has('widget-preview')
  const handleClose = useCallback((): void => {
    if (handleEndOpen !== undefined) handleEndOpen()
    room?.disconnect(true)
    setTextResponse(undefined)
    setSelectedOption(undefined)
    setDataMessage(undefined)
  }, [handleEndOpen, room])

  const handleDataMessage = useCallback(
    (payload: Uint8Array) => {
      const data: DataMessage = JSON.parse(new TextDecoder().decode(payload))
      switch (data.type) {
        case 'committed':
          setTranscriptCommitted(true)
          break

        case 'ready':
          if (campaignId !== null) {
            if (room) {
              const encoder = new TextEncoder()
              const campId: DataMessage = {
                type: 'init',
                content: { campaignId, conversationId: props.conversationId, preview }
              }
              const data = encoder.encode(JSON.stringify(campId))
              room.localParticipant.publishData(data, { reliable: true })
            } else {
              console.log(`warn: room is undefined`)
            }
          } else {
            console.log(`warn: campaignId not found`)
          }
          break

        case 'options':
          setOptions(data.content as string[])
          break

        case 'selected':
          const idx = options?.findIndex(option => option === (data.content as string))
          if (idx !== undefined) setSelectedOption(idx)
          setTimeout(() => setOptions(undefined), 1000)
          break
        case 'error':
          toast.error(data.content as string)
          break
        case 'end':
          handleClose()
          break
        case 'audio':
          setDataMessage({
            type: data.type,
            content: data.content as AudioNode
          })
          break
        case 'video':
          setDataMessage({
            type: data.type,
            content: data.content as VideoNode
          })
          break
        case 'image':
          setDataMessage({
            type: data.type,
            content: data.content as ImageNode
          })
          break
        case 'silent':
          setDataMessage({
            type: data.type,
            content: data.content as SilentNode
          })
          break
        case 'redirect':
          setDataMessage({
            type: data.type,
            content: data.content as RedirectNode
          })
          break
        case 'close_mic':
          setMicEvent('close_mic')
          break
        case 'open_mic':
          setMicEvent('open_mic')
          break
        default:
          console.log(`Unknown data type received: ${data.type}`)
          break
      }
    },
    [
      room,
      campaignId,
      props?.conversationId,
      preview,
      options,
      dataMessage,
      handleClose,
      previousMicrophoneState,
      micClosedBySystem,
      micEvent
    ]
  )

  useEffect(() => {
    if (!room) return

    //console.log('room initialized')
    room.on(RoomEvent.DataReceived, handleDataMessage)

    const handleTrackPublished = async (trackPublication: LocalTrackPublication): Promise<void> => {
      if (
        trackPublication.source === Track.Source.Microphone &&
        trackPublication.track instanceof LocalAudioTrack
      ) {
        try {
          if (!isKrispNoiseFilterSupported()) {
            console.warn('Krisp noise filter is not supported on this browser')
            return
          }

          if (process.env.REACT_APP_KRISP_ENABLED === 'true') {
            try {
              const { KrispNoiseFilter } = await import('@livekit/krisp-noise-filter')
              const krispProcessor = KrispNoiseFilter()
              await trackPublication.track.setProcessor(krispProcessor)
              await krispProcessor.setEnabled(true)
              //console.log('🟢 Krisp is enabled?', krispProcessor.isEnabled())
            } catch (error) {
              console.warn('⚠️ Krisp failed to initialize:', error)
            }
          } else {
            //console.log('🧪 Krisp skipped in local dev')
          }
        } catch (error) {
          console.error('Error applying Krisp noise filter:', error)
        }
      }
    }

    room.on(RoomEvent.LocalTrackPublished, handleTrackPublished)

    return (): void => {
      room.off(RoomEvent.DataReceived, handleDataMessage)
      room.off(RoomEvent.LocalTrackPublished, handleTrackPublished)
    }
  }, [room, handleDataMessage])

  const handleTextResponse = (text: string): void => {
    setTextResponse(text)
  }
  const sendTextResponse = (): void => {
    if (textResponse !== undefined) {
      messageSender?.sendResponse(textResponse)
      setTextResponse(undefined)
      setAccumulatedResponses(prevResponses => [...prevResponses, textResponse])
    }
  }

  const handleNext = (nodeType?: string): void => {
    if (nodeType === 'silent' && previousMicrophoneState) {
      //room?.localParticipant.setMicrophoneEnabled(true) // Réactive le micro si c'était activé avant
    }
    setDataMessage(undefined)
    messageSender?.sendNext()
  }

  useEffect(() => {
    const isCurrentlyEnabled = room?.localParticipant.isMicrophoneEnabled ?? true

    if (dataMessage?.type === 'silent') {
      if (!micClosedBySystem) setPreviousMicrophoneState(isCurrentlyEnabled)
      // Mute micro uniquement s'il est activé
      if (isCurrentlyEnabled === true) {
        room?.localParticipant.setMicrophoneEnabled(false)
      }
    }
  }, [dataMessage, room])

  useEffect(() => {
    const isCurrentlyEnabled = room?.localParticipant.isMicrophoneEnabled ?? true

    if (micEvent === 'close_mic') {
      //console.log(`close_mic ${previousMicrophoneState} ${isCurrentlyEnabled} ${micClosedBySystem}`)
      if (!micClosedBySystem) setPreviousMicrophoneState(isCurrentlyEnabled) // On garde en mémoire qu'il était activé
      setMicClosedBySystem(true)
      room?.localParticipant.setMicrophoneEnabled(false)
    }
    if (micEvent === 'open_mic') {
      //console.log(`open_mic ${previousMicrophoneState} ${isCurrentlyEnabled} ${micClosedBySystem}`)
      setMicClosedBySystem(false)
      // On réactive le micro uniquement s'il était activé avant
      if (previousMicrophoneState === true) room?.localParticipant.setMicrophoneEnabled(true)
    }
  }, [micEvent, room, previousMicrophoneState, micClosedBySystem])

  const buildNode = (dataMessage: DataMessage | undefined): React.JSX.Element | null => {
    switch (dataMessage?.type) {
      case 'video': {
        const node: VideoNode = dataMessage.content as VideoNode
        return <Video node={node} onVideoEnded={handleNext} />
      }
      case 'image': {
        const node: ImageNode = dataMessage.content as ImageNode
        //console.log('image called')
        return <Image onNextCalled={handleNext} node={node} />
      }
      case 'silent': {
        if (dataMessage.content === undefined) {
          console.log('Warning: Silent is no defined')
        }

        if (typeof dataMessage.content !== 'number') {
          console.log('Warning: Silent is not number')
        }

        const node: SilentNode = dataMessage.content as SilentNode

        return <Silence onSilenceEnded={() => handleNext('silent')} node={node} />
      }
      case 'audio': {
        return (
          <AudioPlayer
            onNextCalled={handleNext}
            src={(dataMessage.content as AudioNode).audioUrl}
          />
        )
      }
      case 'redirect': {
        const thirdpartyId = searchParams.get('v_ref')
        const url = new URL((dataMessage.content as RedirectNode).redirectUrl)

        if (thirdpartyId !== null) {
          // thirdpartyId est déjà décodé par searchParams.get()
          const params = new URLSearchParams(`v_ref=${thirdpartyId}`)

          params.forEach((value, key) => {
            url.searchParams.set(key, value)
          })
        }

        console.log('Redirect final:', url.toString())
        window.location.href = url.toString()

        return <></>
      }
      default: {
        console.log('Unknown message type ', dataMessage?.type)
        return null
      }
    }
  }

  const [agentState, setAgentState] = useState<AgentState>('disconnected')
  const { saveAudioInputEnabled } = usePersistentUserChoices({
    preventSave: false
  })

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) => {
      if (isUserInitiated) {
        saveAudioInputEnabled(enabled)
      }
    },
    [saveAudioInputEnabled]
  )

  const [showDiv, setShowDiv] = useState(false)

  useEffect(() => {
    if (dataMessage !== undefined) {
      const timer = setTimeout(() => {
        setShowDiv(true)
      }, 300) // 1 second delayt
      return (): void => clearTimeout(timer) // Cleanup on unmount
    }
    return
  }, [dataMessage])

  const hasInfoParam = searchParams.has('info')
  return (
    <div ref={ref} {...htmlProps}>
      {room && (
        <RoomContext.Provider value={room}>
          <LKFeatureContext.Provider value={props.featureFlags}>
            <div className="main-container">
              {hasInfoParam && (
                <div className="transcription-container">
                  <UserTranscription accumulatedResponses={accumulatedResponses} />
                  <LatencyDisplay />
                </div>
              )}
              <div
                className={`top-section  ${dataMessage !== undefined && dataMessage.type !== 'audio' && dataMessage.type !== 'silent' ? 'top-section-with-content' : 'top-section-without-content'}`}
              >
                <AgentVisualizer
                  onStateChange={setAgentState}
                  primaryColor={props.primaryColor}
                  secondaryColor={props.secondaryColor}
                  silenceTimer={
                    dataMessage?.type === 'silent' && showDiv ? buildNode(dataMessage) : null
                  }
                />
                <div className="transcript-container-space">
                  <AgentTranscription
                    transcriptCommited={transcriptCommitted}
                    setTranscriptCommited={setTranscriptCommitted}
                  />
                </div>
              </div>
              <div className="content-section">
                {dataMessage === undefined && options !== undefined && (
                  <QuestionOptions
                    options={options ?? []}
                    selectedOption={selectedOption}
                    onclick={handleOptionClick}
                  />
                )}
                {dataMessage !== undefined &&
                  dataMessage.type !== 'silent' &&
                  showDiv &&
                  buildNode(dataMessage)}
              </div>
              <div
                className={`bottom-section ${dataMessage !== undefined && dataMessage.type !== 'audio' && dataMessage.type !== 'silent' ? 'bottom-section-with-content' : 'bottom-section-without-content'}`}
              >
                {props.isConnected && (
                  <TextAnswerer
                    handleChange={handleTextResponse}
                    sendResponse={sendTextResponse}
                    textValue={textResponse}
                  />
                )}
                <div className="control-panel">
                  <TrackToggle
                    source={Track.Source.Microphone}
                    onChange={microphoneOnChange}
                    disabled={micClosedBySystem}
                  />
                  <UserVisualizer />
                  <DisconnectSurveyButton />
                </div>
              </div>
            </div>

            <RoomAudioRenderer />
            <NoAgentNotification state={agentState} />
          </LKFeatureContext.Provider>
        </RoomContext.Provider>
      )}
    </div>
  )
})
function AgentVisualizer(props: {
  onStateChange: (state: AgentState) => void
  primaryColor: string
  secondaryColor: string
  silenceTimer?: React.JSX.Element | null
}): JSX.Element {
  const { state, audioTrack } = useVoiceAssistant()
  const volumeBands: number[] = useMultibandTrackVolume(audioTrack, {
    bands: 16,
    loPass: 100,
    hiPass: 200
  })
  useEffect(() => {
    props.onStateChange(state)
  }, [props, state])

  return (
    <div className="voice-assistant-visualizer-container">
      {/* <NeonWaveVisualizer volumeBands={volumeBands} /> */}
      {props.silenceTimer}
      <VoiceVisualization
        size={200}
        state={state}
        primaryColor={props.primaryColor}
        secondaryColor={props.secondaryColor}
        audioLevel={volumeBands.reduce((acc, val) => acc + val, 0) / volumeBands.length}
      />
    </div>
  )
}

type Transcription = {
  id: string
  text: string
}

function getTextToDisplay(
  transcriptions: Transcription[],
  commitState: Record<number, boolean>
): string[] {
  const committedIndexes = Object.keys(commitState)
    .filter(key => commitState[parseInt(key)] === true)
    .map(k => parseInt(k))
    .sort((a, b) => a - b)

  const lastCommitted = committedIndexes.at(-1) ?? -1

  if (transcriptions.length === lastCommitted) {
    const textToReturn = transcriptions.slice(committedIndexes.at(-1) ?? -1).map(t => t.text)
    return textToReturn
  } else {
    const textToReturn = transcriptions.slice(lastCommitted + 1).map(t => t.text)
    return textToReturn
  }
}

export function AgentTranscription({
  transcriptCommited,
  setTranscriptCommited
}: {
  transcriptCommited: boolean
  setTranscriptCommited: React.Dispatch<React.SetStateAction<boolean>>
}): JSX.Element {
  const { agentTranscriptions } = useVoiceAssistant()
  const [fullTranscription, setFullTranscription] = useState<string>('')
  const [commitState, setCommitState] = useState<Record<number, boolean>>({})
  const clearPendingRef = useRef<boolean>(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const lastTranscriptionLengthRef = useRef<number>(0)

  // Log the raw agent transcriptions feed
  useEffect(() => {
    console.log('Raw agent transcriptions feed:', agentTranscriptions)
    console.log('Agent transcriptions count:', agentTranscriptions.length)
    agentTranscriptions.forEach((transcript, index) => {
      console.log(`Transcript ${index}:`, {
        id: transcript.id,
        text: transcript.text,
        timestamp: new Date().toISOString()
      })
    })
  }, [agentTranscriptions])

  // Timer-based approach instead of committed trigger
  useEffect(() => {
    const currentLength = agentTranscriptions.length
    const lastLength = lastTranscriptionLengthRef.current

    // Check if new text arrived
    if (currentLength > lastLength) {
      // Clear existing timer if any
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      // Start new timer (3 seconds)
      timerRef.current = setTimeout(() => {
        // Run the commit function
        setCommitState(prev => {
          const newState = { ...prev }
          for (let i = 0; i < agentTranscriptions.length; i++) {
            newState[i] = i === agentTranscriptions.length - 1 ? true : (newState[i] ?? false)
          }
          return newState
        })

        clearPendingRef.current = true
        timerRef.current = null
      }, 3000) // 3 seconds

      // Update last length
      lastTranscriptionLengthRef.current = currentLength
    }

    // Cleanup timer on unmount
    return (): void => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [agentTranscriptions])

  // Keep the original committed logic as fallback
  useEffect(() => {
    if (transcriptCommited) {
      setTranscriptCommited(false)

      // Clear timer if running
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }

      setCommitState(prev => {
        const newState = { ...prev }
        for (let i = 0; i < agentTranscriptions.length; i++) {
          newState[i] = i === agentTranscriptions.length - 1 ? true : (newState[i] ?? false)
        }
        return newState
      })
      clearPendingRef.current = true
      return
    }
  }, [transcriptCommited, agentTranscriptions.length, commitState])

  // Handle display logic
  useEffect(() => {
    const textsToShow = getTextToDisplay(agentTranscriptions, commitState)

    if (textsToShow.length > 0) {
      if (clearPendingRef.current) {
        setFullTranscription('')
        clearPendingRef.current = false
      }
      const joinedText = textsToShow.join('\n')

      setFullTranscription(joinedText)
    }
  }, [agentTranscriptions, commitState])

  return (
    <>
      {fullTranscription && (
        <div className="transcript-container">
          <span>
            {fullTranscription.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </span>
        </div>
      )}
    </>
  )
}

export function UserTranscription(props: { accumulatedResponses: string[] }): JSX.Element {
  const { localParticipant } = useLocalParticipant()

  const audioTrack = useParticipantTracks([Track.Source.Microphone], localParticipant?.identity)[0]
  const { segments: userTranscriptions } = useTrackTranscription(audioTrack)

  return (
    <div>
      <div className="transcript-container user-transcript-container">
        {userTranscriptions.map(e => (
          <span>{e.text.split(' ').join(' ')}</span>
        ))}

        {props.accumulatedResponses.map((text, index) => (
          <span key={`sent-response-${index}`} className="user-text-response">
            {text.split(' ').join(' ')}
          </span>
        ))}
      </div>
    </div>
  )
}

// function MicRecorder({ isNoiseFilterEnabled }: { isNoiseFilterEnabled: boolean }): JSX.Element {
//   const [isRecording, setIsRecording] = useState(false)
//   const mediaRecorderRef = useRef<MediaRecorder | null>(null)
//   const recordedChunksRef = useRef<Blob[]>([])
//   const streamRef = useRef<MediaStream | null>(null)
//   const { localParticipant } = useLocalParticipant()
//   const micTrackRef = useParticipantTracks([Track.Source.Microphone], localParticipant?.identity)[0]
//   const startRecording = (): void => {
//     // IMPORTANT: This is the track AFTER Krisp is applied

//     const mediaStreamTrack = micTrackRef?.publication?.track?.mediaStreamTrack

//     if (!mediaStreamTrack) {
//       console.warn('No processed media stream track found')
//       return
//     }

//     // This stream contains the processed audio (Krisp if enabled)
//     const stream = new MediaStream([mediaStreamTrack])

//     const mediaRecorder = new MediaRecorder(stream)
//     mediaRecorderRef.current = mediaRecorder

//     mediaRecorder.ondataavailable = (e): void => {
//       if (e.data.size > 0) {
//         recordedChunksRef.current.push(e.data)
//       }
//     }

//     mediaRecorder.onstop = (): void => {
//       const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' })
//       const url = URL.createObjectURL(blob)
//       const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
//       const filename = `recording-${isNoiseFilterEnabled ? 'with-krisp' : 'no-krisp'}-${timestamp}.webm`

//       const link = document.createElement('a')
//       link.href = url
//       link.download = filename
//       document.body.appendChild(link)
//       link.click()
//       link.remove()
//       URL.revokeObjectURL(url)

//       recordedChunksRef.current = []
//     }

//     mediaRecorder.start()
//     setIsRecording(true)
//   }

//   const stopRecording = (): void => {
//     mediaRecorderRef.current?.stop()
//     streamRef.current?.getTracks().forEach(track => track.stop()) // clean up
//     setIsRecording(false)
//   }

//   return (
//     <div style={{ marginTop: '1rem' }}>
//       <button onClick={isRecording ? stopRecording : startRecording}>
//         {isRecording ? '🛑 Stop Recording' : '🎙️ Start Recording'}
//       </button>
//     </div>
//   )
// }

function UserVisualizer(): JSX.Element {
  const { localParticipant } = useLocalParticipant()
  const audioTrack = useParticipantTracks([Track.Source.Microphone], localParticipant?.identity)[0]
  const volumeBands: number[] = useMultibandTrackVolume(audioTrack, {
    bands: 5,
    loPass: 100,
    hiPass: 200
  })

  return (
    <>
      {/* {false && <MicRecorder isNoiseFilterEnabled={true} />} */}

      <div className="user-visualizer">
        <BarsWaveForm volumeBands={volumeBands} />
      </div>
    </>
  )
}

// function ControlBar(props: { agentState: AgentState }): JSX.Element {
//   return (
//     <div>
//       {props.agentState !== 'disconnected' && props.agentState !== 'connecting' && (
//         <div className="">
//           <VoiceAssistantControlBar controls={{ leave: false }} />
//         </div>
//       )}
//     </div>
//   )
// }

export default VocadsAssistant
